import { useUnitAvailabilitySuspenseQuery } from '@/api/accommodation/hooks'
import { addDays, format, isSameDay, max, startOfWeek, endOfWeek, eachDayOfInterval, startOfDay } from 'date-fns'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn, getNightsLabel } from '@/lib/utils'
import { useEffect, useMemo, useState, type ReactNode } from 'react'

import { useDateSelection } from './useDateSelection'
import Price from '@/components/money/price'
import CalendarEmptyIcon from '@/components/icons/calendarEmpty'
import type { AccommodationDetail } from '@/api/accommodation/types'
import { LocationWatchmanSheet } from '../location-watchman/LocationWatchmanSheet'
import { coversMinimumNights, getDateSelectionWindow, getSelectableDepartureDates } from './dateSelectionWindow'

type SelectionMode = 'ARRIVAL' | 'DEPARTURE' | 'BOTH_SELECTED'

interface DateSelectionCalendarProps {
  accommodation: AccommodationDetail
}

// Unselectable day that explains (on tap/hover) why it can't be booked.
// It is marked aria-disabled instead of disabled, so taps and keyboard
// activation still reach it and open the tooltip.
const DisabledDayButton = ({
  reason,
  className,
  children,
}: {
  reason: string
  className?: string
  children: ReactNode
}) => {
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (!open) return
    const timeout = setTimeout(() => setOpen(false), 4000)
    return () => clearTimeout(timeout)
  }, [open])

  return (
    <Tooltip open={open} onOpenChange={setOpen}>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-disabled="true"
          className={cn(
            // The ghost Button's look, without its hover colors.
            'inline-flex h-auto w-full shrink-0 flex-col items-center justify-center gap-1 rounded-md border border-transparent px-0.5 py-2 leading-none font-medium whitespace-nowrap transition-all outline-none focus-visible:border-slate-950 focus-visible:ring-[3px] focus-visible:ring-slate-950/50',
            className,
          )}
          onClick={(e) => {
            e.stopPropagation()
            setOpen(true)
          }}
        >
          {children}
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-[14rem] text-center">{reason}</TooltipContent>
    </Tooltip>
  )
}

export const DateSelectionCalendar = ({ accommodation }: DateSelectionCalendarProps) => {
  const { selectedFrom, selectedTo, setSelectedFrom, setSelectedTo } = useDateSelection()
  // Derive current selection mode from state
  const selectionMode: SelectionMode = useMemo(() => {
    if (!selectedFrom) return 'ARRIVAL'
    if (!selectedTo) return 'DEPARTURE'
    return 'BOTH_SELECTED'
  }, [selectedFrom, selectedTo])

  const { data: availabilityData } = useUnitAvailabilitySuspenseQuery(accommodation.unit.id)

  const today = useMemo(() => startOfDay(new Date()), [])
  const dateSelectionWindow = useMemo(() => getDateSelectionWindow(availabilityData, today), [availabilityData, today])
  const firstDay = dateSelectionWindow?.firstDay ?? today
  const lastDay = dateSelectionWindow?.lastDay ?? today

  // The grid spans the location's whole offer window even where the last
  // bookable day comes earlier; those days render greyed out. Selection stays
  // bound to lastDay.
  const gridLastDay = useMemo(() => {
    if (!accommodation.offerAheadDays) return lastDay

    return max([addDays(today, accommodation.offerAheadDays), lastDay])
  }, [accommodation.offerAheadDays, today, lastDay])

  // Generate calendar grid dates (all days from Monday to Sunday across weeks)
  const calendarDates = useMemo(() => {
    if (!dateSelectionWindow) return []

    const start = startOfWeek(firstDay, { weekStartsOn: 1 })
    const end = endOfWeek(gridLastDay, { weekStartsOn: 1 })
    return eachDayOfInterval({ start, end })
  }, [dateSelectionWindow, firstDay, gridLastDay])

  // Find valid departure dates for the selected arrival date
  const validDepartureDates = useMemo(() => {
    if (!selectedFrom) return []

    return getSelectableDepartureDates(availabilityData, selectedFrom, firstDay, lastDay)
  }, [selectedFrom, availabilityData, firstDay, lastDay])
  const firstValidDeparture = validDepartureDates[0]

  // Handler for ARRIVAL mode - selecting the arrival date
  const handleArrivalSelection = (date: Date) => {
    setSelectedFrom(date)
    setSelectedTo(null)
  }

  // Handler for DEPARTURE mode - selecting the departure date
  const handleDepartureSelection = (date: Date) => {
    // Allow deselecting the arrival date by clicking it again
    if (isSameDay(date, selectedFrom!)) {
      setSelectedFrom(null)
      return
    }

    // Check if this is a valid departure for the selected arrival
    const isValidDeparture = validDepartureDates.some((depDate) => isSameDay(depDate, date))

    if (isValidDeparture) {
      // Valid departure - set as dateTo
      setSelectedTo(date)
    } else {
      // Not a valid departure - restart arrival selection with this date
      handleArrivalSelection(date)
    }
  }

  // Main click handler that dispatches based on current mode
  const handleDateClick = (date: Date) => {
    switch (selectionMode) {
      case 'ARRIVAL':
        handleArrivalSelection(date)
        break
      case 'DEPARTURE':
        handleDepartureSelection(date)
        break
      case 'BOTH_SELECTED':
        // Reset and start new selection
        handleArrivalSelection(date)
        break
    }
  }

  return (
    <div>
      {availabilityData.length > 0 && dateSelectionWindow ? (
        <>
          {/* Calendar grid with week layout */}
          {/* Click anywhere outside a day to reset the selection */}
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- mouse-only shortcut; keyboard users reset by activating the selected arrival day again */}
          <div
            onClick={() => {
              setSelectedFrom(null)
              setSelectedTo(null)
            }}
          >
            {/* Week day headers */}
            <div className="mb-2 grid grid-cols-7 gap-0.5 md:gap-2">
              {['Po', 'Út', 'St', 'Čt', 'Pá', 'So', 'Ne'].map((day) => (
                <div key={day} className="text-center text-xs font-medium tracking-wider text-slate-500 uppercase">
                  {day}
                </div>
              ))}
            </div>
            {/* Calendar dates grid */}
            <div className="grid grid-cols-7 gap-0.5 md:gap-1">
              {calendarDates.map((date, index) => {
                // Find availability data for this date
                const arrival = availabilityData.find((a) => isSameDay(a.date, date))

                // The API also returns arrival-looking days which cannot lead
                // to a valid stay (for example only one free night remains
                // while the minimum stay is two nights).
                const isValidArrival =
                  arrival?.state === 'arrival' &&
                  arrival.available &&
                  getSelectableDepartureDates(availabilityData, arrival.date, firstDay, lastDay).length > 0

                const dayNumber = format(date, 'dd.')
                const monthNumber = format(date, 'MM.')

                // Check if this date is a valid departure for the selected arrival
                const isValidDeparture =
                  selectionMode === 'DEPARTURE' && validDepartureDates.some((depDate) => isSameDay(depDate, date))

                // Is this the selected arrival date?
                const isSelectedArrival = selectedFrom && isSameDay(date, selectedFrom)

                // Dark green: arrival and departure dates
                const isDarkGreen =
                  (selectedFrom && isSameDay(date, selectedFrom)) || (selectedTo && isSameDay(date, selectedTo))

                // Light green: dates between arrival and departure (excluding first and last)
                const isLightGreen =
                  selectedFrom &&
                  selectedTo &&
                  startOfDay(date) > startOfDay(selectedFrom) &&
                  startOfDay(date) < startOfDay(selectedTo)

                // Dates in potential range (between arrival and first valid departure)
                // Show these in light green when in DEPARTURE mode
                const isInPotentialRange =
                  selectionMode === 'DEPARTURE' &&
                  selectedFrom &&
                  !selectedTo &&
                  firstValidDeparture &&
                  startOfDay(date) > startOfDay(selectedFrom) &&
                  startOfDay(date) < startOfDay(firstValidDeparture)

                // Check if date is outside the requested range (slateed out)
                const isOutsideRange = date < firstDay || date > lastDay

                // Determine if clickable
                const isClickable = (() => {
                  if (isOutsideRange) return false

                  if (selectionMode === 'DEPARTURE') {
                    // In departure mode: only valid departures or the arrival (to deselect)
                    return isValidDeparture || isSelectedArrival
                  }
                  // Only arrivals which have at least one valid departure can
                  // start a booking. This also applies when starting a new
                  // selection after a complete range was selected.
                  return isValidArrival
                })()

                // Determine button style
                const buttonStyleClass = (() => {
                  // First and last day - dark green
                  if (isDarkGreen) {
                    return 'bg-green-700 text-white opacity-100! hover:bg-green-700 hover:text-white'
                  }

                  // Days in between - light green
                  if (isLightGreen) {
                    return 'bg-green-500 text-white opacity-100! hover:bg-green-500 hover:text-white'
                  }

                  // Dates in potential departure range - green text, not clickable
                  if (isInPotentialRange) {
                    return cn('cursor-not-allowed opacity-100!', isValidArrival ? 'text-green-700' : 'text-green-500')
                  }

                  // Clickable dates - green text
                  if (isClickable) {
                    return 'border-green-200 text-green-700 hover:bg-green-100 hover:text-green-700'
                  }

                  // Outside range - slateed out
                  if (isOutsideRange) {
                    return 'cursor-not-allowed text-slate-300 opacity-100!'
                  }

                  // Color communicates availability; only the border and
                  // hover affordance communicate current clickability.
                  if (arrival) {
                    return cn('cursor-not-allowed opacity-100!', isValidArrival ? 'text-green-700' : 'text-green-500')
                  }

                  // Default - disabled, slate
                  return 'cursor-not-allowed text-slate-300 opacity-100!'
                })()

                // Explain why an unselectable day can't be booked (arrival mode only).
                const disabledReason: string | null = (() => {
                  if (isClickable || selectionMode === 'DEPARTURE') return null
                  if (startOfDay(date) < startOfDay(today)) {
                    return 'Do minulosti rezervovat bohužel nejde.'
                  }
                  if (date > lastDay) {
                    return `Toto místo nabízí last-minute termíny pouze do ${format(lastDay, 'd. M.')}.`
                  }
                  if (arrival?.state === 'departure') {
                    return 'Tento den lze zvolit pouze jako odjezd po výběru příjezdu.'
                  }
                  if (arrival?.state === 'arrival' && arrival.available && arrival.closedToArrival) {
                    return 'V tento den nelze přijet.'
                  }
                  if (
                    arrival?.state === 'arrival' &&
                    arrival.available &&
                    !isValidArrival &&
                    (arrival.departures?.length ?? 0) > 0
                  ) {
                    return `Odjezd by už byl mimo nabídku tohoto místa do ${format(lastDay, 'd. M.')}.`
                  }
                  if (arrival?.state === 'arrival' && arrival.available && (arrival.departures?.length ?? 0) === 0) {
                    if (coversMinimumNights(availabilityData, arrival)) {
                      return 'Pobyt od tohoto dne nejde ukončit v povolený den odjezdu.'
                    }
                    return `Z tohoto dne už nelze splnit minimální délku ${arrival.minNights} ${getNightsLabel(arrival.minNights)}.`
                  }
                  return 'Volno tu bohužel již není.'
                })()

                const dayLabel = (
                  <div className="text-sm font-medium">
                    {dayNumber} <span className="text-xs opacity-50">{monthNumber}</span>
                  </div>
                )

                const dateDetails = !isOutsideRange ? (
                  <div className="flex flex-col gap-0.5">
                    {/* Prices describe free nights. The summary above the
                        calendar communicates whether a click selects an
                        arrival or a departure. */}
                    {arrival?.state === 'arrival' && (
                      <>
                        <Price
                          price={arrival.discountPrice}
                          className={cn(
                            'text-xxs font-semibold',
                            isDarkGreen || isLightGreen ? 'text-white!' : 'text-slate-800!',
                          )}
                        />
                        {arrival.standardPrice !== arrival.discountPrice && (
                          <Price
                            price={arrival.standardPrice}
                            className={cn(
                              'text-xxs line-through',
                              isDarkGreen || isLightGreen ? 'text-white/70!' : 'text-slate-500!',
                            )}
                          />
                        )}
                      </>
                    )}
                    {arrival?.state === 'departure' && (
                      <>
                        <span
                          className={cn(isDarkGreen && 'text-white', !isDarkGreen && 'text-slate-500!', 'text-xxs')}
                        >
                          Pouze
                        </span>
                        <span
                          className={cn(isDarkGreen && 'text-white', !isDarkGreen && 'text-slate-500!', 'text-xxs')}
                        >
                          odjezd
                        </span>
                      </>
                    )}
                  </div>
                ) : null

                if (disabledReason) {
                  return (
                    <div key={index} className="flex flex-col items-center">
                      <DisabledDayButton reason={disabledReason} className={buttonStyleClass}>
                        {dayLabel}
                        {dateDetails}
                      </DisabledDayButton>
                    </div>
                  )
                }

                return (
                  <div key={index} className="flex flex-col items-center">
                    {/* Date button with price inside */}
                    <Button
                      variant="ghost"
                      disabled={!isClickable}
                      className={cn(
                        'h-auto w-full flex-col gap-1 rounded-md border border-transparent px-0.5 py-2 leading-none',
                        buttonStyleClass,
                      )}
                      onClick={(e) => {
                        e.stopPropagation()
                        // For dates from API, use arrival.date
                        // For extra dates (not in API), use the calendar date
                        const dateToHandle = arrival ? arrival.date : date
                        handleDateClick(dateToHandle)
                      }}
                    >
                      {dayLabel}
                      {dateDetails}
                    </Button>
                  </div>
                )
              })}
            </div>
          </div>
        </>
      ) : (
        <div className="flex flex-col items-center py-8 text-center">
          <CalendarEmptyIcon className="mb-4 h-12 w-12 text-slate-300" />
          <h3 className="mb-2 text-lg font-medium text-slate-700">Momentálně nejsou volné termíny</h3>
          <p className="mb-6 text-sm text-slate-500">
            Pro toto ubytování nejsou v nejbližší době dostupné žádné volné termíny. Zkuste to prosím později nebo se
            podívejte na jiná ubytování.
          </p>
          <LocationWatchmanSheet accommodation={accommodation} variant="emptyState" />
        </div>
      )}
    </div>
  )
}
